import React from 'react';
import RiskIndicator from './RiskIndicator';
import CBCMetricsCard from './CBCMetricsCard';
import ParasocialMetricsCard from './ParasocialMetricsCard';
import BoundaryFlagsList from './BoundaryFlagsList';

/**
 * ClientBoundaryOverview - Detailed boundary analysis for a single client
 */
const ClientBoundaryOverview = ({
  client,
  overview,
  onAcknowledge,
  onResolve,
  onDismiss,
  onBack,
  loading
}) => {
  const containerStyle = {
    padding: '0'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  };

  const clientInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  };

  const avatarStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#e9ecef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
    color: '#495057'
  };

  const backButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <button style={backButtonStyle} onClick={onBack}>
            Back to Client List
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
          Loading client data...
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <button style={backButtonStyle} onClick={onBack}>
            Back to Client List
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
          No data available for this client.
        </div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={clientInfoStyle}>
          <div style={avatarStyle}>
            {getInitials(client?.name)}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px' }}>
              {client?.name || 'Unknown Client'}
            </h3>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
              {client?.email}
              {client?.therapist && (
                <span> | Therapist: {client.therapist.name}</span>
              )}
            </div>
          </div>
          <RiskIndicator level={overview.overallRiskLevel} size="large" />
        </div>
        <button style={backButtonStyle} onClick={onBack}>
          Back to Client List
        </button>
      </div>

      {/* Metrics Grid */}
      <div style={gridStyle}>
        {/* CBC Metrics */}
        <CBCMetricsCard metrics={overview.cbcMetrics} />

        {/* Parasocial Metrics */}
        <ParasocialMetricsCard
          parasocial={overview.parasocial}
          engagement={overview.engagement}
        />
      </div>

      {/* Flags Section */}
      <BoundaryFlagsList
        flags={overview.flags}
        onAcknowledge={onAcknowledge}
        onResolve={onResolve}
        onDismiss={onDismiss}
        loading={loading}
      />

      {/* Recent Alerts */}
      {overview.alerts && overview.alerts.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#333',
            borderBottom: '1px solid #eee',
            paddingBottom: '8px'
          }}>
            Recent Alerts ({overview.alerts.length})
          </div>
          {overview.alerts.map((alert, idx) => (
            <div key={alert.id || idx} style={{
              padding: '10px',
              marginBottom: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '13px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '500' }}>{alert.type}</span>
                <span style={{ color: '#666', fontSize: '11px' }}>
                  {new Date(alert.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div style={{ color: '#666', marginTop: '4px' }}>
                Source: {alert.source} | Priority: {alert.priority} | Status: {alert.status}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Last Updated */}
      {overview.lastUpdated && (
        <div style={{ textAlign: 'right', fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Last updated: {new Date(overview.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default ClientBoundaryOverview;
